import { useContext, useEffect } from 'react';
import { CybersourceMicroformFormContext } from './CybersourceMicroformFormProvider';

/**
 * Custom hook to access the CybersourceMicroformForm context.
 * Provides access to Cybersource Microform functionality and form state.
 *
 * @param {object} options - Hook options
 * @param {boolean} options.autoInitialize - Whether to automatically initialize Cybersource on mount
 * @returns {object} The CybersourceMicroformForm context value with the following properties:
 * - formValues: Current form values
 * - setFieldValue: Function to update a form field value
 * - createField: Function to create a Cybersource Microform field
 * - createPaymentPayload: Function to create a payment payload
 * - initializeCybersource: Function to initialize Cybersource SDK and Microform
 * - resetForm: Function to reset form values and errors
 * - isFormValid: Function to check if the form is valid
 * - isLoading: Boolean indicating if an operation is in progress
 * - initialized: Object indicating if Flex and Microform are initialized
 * - features: Object containing feature flags
 */
const useCybersourceMicroformForm = (options = {}) => {
  const { autoInitialize = false } = options;
  const context = useContext(CybersourceMicroformFormContext);

  if (!context) {
    throw new Error(
      'useCybersourceMicroformForm must be used within a CybersourceMicroformFormProvider',
    );
  }

  const { initializeCybersource, initialized } = context;

  // Auto-initialize Cybersource if requested. Gate on the microform (the real completion signal)
  // rather than the Flex latch, so a partial init (flex ready, microform not) is re-attempted and
  // a late/changed capture context re-initializes. The provider dedupes concurrent runs, so a
  // stable JWT still initializes exactly once (no re-init loop).
  useEffect(() => {
    if (autoInitialize && !initialized.microform) {
      initializeCybersource();
    }
  }, [autoInitialize, initialized.microform, initializeCybersource]);

  return context;
};

export default useCybersourceMicroformForm;
